/**
 * NotifyPromptModal — 홈 화면 앱으로 처음 들어왔을 때 1회 "설치 완료! 알림도 켜 둘까요?".
 *  [알림 켜기] = services/messaging.enablePush (사용자 제스처 안에서 호출 — 시스템 허용 창이 뜸).
 *  아이폰은 이 자리(홈 화면 앱)에서만 푸시를 켤 수 있다. 닫으면 다시 뜨지 않고 내 계정 › 알림 수신 설정에서 켠다.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { useToast } from "@/hooks/useToast";
import { enablePush } from "@/services/messaging";
import type { A2hsPlatform } from "@/domain/a2hs";
import { NotifyDemo } from "./A2hsDemo";
import "./a2hs.css";
import "./a2hs-demo.css";

interface Props {
  platform: A2hsPlatform;
  onClose: () => void;
}

export function NotifyPromptModal({ platform, onClose }: Props) {
  const { user } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const dismiss = useBackdropDismiss(onClose, { disabled: busy });
  const isIos = platform === "ios";

  async function onEnable() {
    if (!user || busy) return;
    setBusy(true);
    try {
      const res = await enablePush(user.uid);
      if (res.ok) toast.success("이 기기에서 앱 푸시를 받습니다. 종류는 내 계정 › 알림 수신 설정에서 고릅니다.");
      else toast.error(res.reason ?? "앱 푸시를 켜지 못했습니다.");
    } catch (e) {
      toast.error(`처리하지 못했습니다: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      onClose();
    }
  }

  return createPortal(
    <div className="modal-backdrop a2hs-backdrop" {...dismiss}>
      <div className="modal-panel a2hs-modal" role="dialog" aria-modal="true" aria-labelledby="a2hs-notify-title">
        <div className="a2hs-hero">
          <div className="a2hs-hero-bell" aria-hidden="true">
            <span className="a2hs-hero-bell-wave" />
            <span className="a2hs-hero-bell-disc"><Icon name="bell" size={32} /></span>
            <span className="a2hs-hero-bell-badge">3</span>
          </div>
          <h3 className="a2hs-title" id="a2hs-notify-title">설치 완료! 알림도 켜 둘까요?</h3>
          <p className="a2hs-sub">
            출하 시작·마무리 같은 소식을 이렇게 받습니다
            <br />
            {isIos ? "아이폰은 이 홈 화면 앱에서만 알림이 옵니다" : "'허용'을 한 번 누르면 바로 시작됩니다"}
          </p>
        </div>

        <NotifyDemo platform={platform} />

        <div className="a2hs-actions">
          <button type="button" className="btn-primary a2hs-cta" onClick={() => void onEnable()} disabled={busy}>
            <Icon name="bell" size={18} /> {busy ? "켜는 중…" : "알림 켜기"}
          </button>
          <div className="a2hs-actions-row">
            <button type="button" className="btn-ghost is-soft" onClick={onClose} disabled={busy}>나중에 설정에서 켤게요</button>
          </div>
          <p className="a2hs-foot">누르면 휴대폰 '허용' 창이 한 번 더 뜹니다</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
